import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { startBehaviorTelemetry } from 'tracking/behaviorTelemetry';
import { createTelegramSessionInvite, resolveTelegramDestination } from 'tracking/funnelBootstrap';
import { createFunnelTracker } from 'tracking/funnelTracker';
import {
  FUNNEL_CTA_LABEL,
  TELEGRAM_URL,
  resolveFunnelVariant,
} from './funnelVariants';
import AntiCopyContextFunnel from './AntiCopyContextFunnel';
import CryptoEducationFunnel from './CryptoEducationFunnel';
import RiskInvalidationFunnel from './RiskInvalidationFunnel';
import { resolveRiskPlanExperimentAssignment } from './riskPlanExperiment';
import './CryptoEducation.css';

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function CryptoEducation() {
  const location = useLocation();
  const { variantSlug } = useParams();
  const rootRef = useRef(null);
  const heroCtaRef = useRef(null);
  const finalCtaRef = useRef(null);
  const ctaVisibilityRef = useRef({ hero: false, final: false });
  const ctaClickInFlightRef = useRef(false);
  const [telegramDestination, setTelegramDestination] = useState({
    url: '',
    resolution: 'loading',
  });
  const [showMobileCta, setShowMobileCta] = useState(false);

  const variant = useMemo(
    () => resolveFunnelVariant({ variantSlug, search: location.search }),
    [location.search, variantSlug]
  );

  const riskPlanExperimentAssignment = useMemo(
    () => resolveRiskPlanExperimentAssignment({ variant }),
    [variant]
  );

  const tracker = useMemo(
    () =>
      createFunnelTracker({
        variant,
        location,
        experimentAssignment: riskPlanExperimentAssignment,
      }),
    [location, riskPlanExperimentAssignment, variant]
  );

  useEffect(() => {
    document.title = `${variant.headline} | CRYPTOXNFT`;

    const frame = window.requestAnimationFrame(() => {
      const trackView = async () => {
        await tracker.trackExperimentAssignment();
        await tracker.trackInitialView();
        await tracker.flush({ timeoutMs: 1500 });
      };

      trackView().catch(() => {});
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [tracker, variant.headline]);

  useEffect(() => {
    let cancelled = false;
    const context = tracker.getContext();

    resolveTelegramDestination({
      zSource: context.z_source,
      zAd: context.z_ad || variant.zAd,
      fallbackUrl: TELEGRAM_URL,
    }).then((destination) => {
      if (!cancelled) {
        setTelegramDestination(destination);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [tracker, variant.zAd]);

  useEffect(() => {
    if (!rootRef.current) {
      return undefined;
    }

    return startBehaviorTelemetry({
      root: rootRef.current,
      tracker,
    });
  }, [tracker]);

  useEffect(() => {
    ctaVisibilityRef.current = { hero: false, final: false };

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setShowMobileCta(true);
      return undefined;
    }

    const updateStickyState = () => {
      const { hero, final } = ctaVisibilityRef.current;
      setShowMobileCta(!hero && !final);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === heroCtaRef.current) {
            ctaVisibilityRef.current.hero = entry.isIntersecting;
          }
          if (entry.target === finalCtaRef.current) {
            ctaVisibilityRef.current.final = entry.isIntersecting;
          }
        });
        updateStickyState();
      },
      {
        root: null,
        threshold: 0.08,
      }
    );

    if (heroCtaRef.current) {
      observer.observe(heroCtaRef.current);
    }
    if (finalCtaRef.current) {
      observer.observe(finalCtaRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [riskPlanExperimentAssignment?.assigned_visual_funnel, variant.key]);

  const telegramReady = Boolean(telegramDestination.url);
  const telegramHref = telegramReady ? telegramDestination.url : '#telegram-invite';
  const telegramCtaLabel = telegramReady ? FUNNEL_CTA_LABEL : 'Preparing invite link';

  const handleTelegramClick = useCallback(
    async (event) => {
      event.preventDefault();

      if (!telegramDestination.url) {
        return;
      }
      if (ctaClickInFlightRef.current) {
        return;
      }

      ctaClickInFlightRef.current = true;

      try {
        const element = event.currentTarget;
        const elementId = element.getAttribute('data-track-id') || '';
        const sectionId =
          element.closest('[data-track-section]')?.getAttribute('data-track-section') || '';
        const context = tracker.getContext();
        const basePayload = {
          element_id: elementId,
          section_id: sectionId,
          cta_label: FUNNEL_CTA_LABEL,
          fallback_destination: telegramDestination.url,
          fallback_telegram_link_resolution: telegramDestination.resolution,
        };
        let destination = telegramDestination;
        let sessionInvite = null;
        let sessionInviteError = '';

        try {
          sessionInvite = await createTelegramSessionInvite({
            context,
            payload: basePayload,
          });
          destination = sessionInvite;
        } catch (error) {
          sessionInviteError = error instanceof Error ? error.message : 'session_invite_unavailable';
        }

        await tracker.trackTelegramClick({
          ...basePayload,
          destination: destination.url,
          telegram_link_resolution: destination.resolution,
          session_invite_id: sessionInvite?.session_invite_id || '',
          session_invite_reused: sessionInvite?.reused === true,
          session_invite_error: sessionInviteError,
        });

        await tracker.track('outbound_telegram_intent', {
          ...basePayload,
          destination: destination.url,
          telegram_link_resolution: destination.resolution,
          session_invite_id: sessionInvite?.session_invite_id || '',
          session_invite_reused: sessionInvite?.reused === true,
          session_invite_error: sessionInviteError,
          navigation_method: 'window_location_href',
        });

        await Promise.race([tracker.flush({ timeoutMs: 1500 }), sleep(1600)]);

        if (window.__FUNNEL_TRACKING_DISABLE_REDIRECT__ === true) {
          return;
        }

        window.location.href = destination.url;
      } finally {
        ctaClickInFlightRef.current = false;
      }
    },
    [telegramDestination.resolution, telegramDestination.url, tracker]
  );

  const telegramCtaProps = {
    href: telegramHref,
    onClick: handleTelegramClick,
    'aria-disabled': telegramReady ? undefined : 'true',
    'data-link-ready': telegramReady ? 'true' : 'false',
  };

  const sharedFunnelProps = {
    variant,
    rootRef,
    heroCtaRef,
    finalCtaRef,
    telegramCtaProps,
    telegramCtaLabel,
    showMobileCta,
    experimentAssignment: riskPlanExperimentAssignment,
  };

  if (variant.key === 'risk_plan') {
    return <RiskInvalidationFunnel {...sharedFunnelProps} />;
  }

  if (variant.key === 'anti_signal_copying') {
    return <AntiCopyContextFunnel {...sharedFunnelProps} />;
  }

  return <CryptoEducationFunnel {...sharedFunnelProps} />;
}

export default CryptoEducation;
